// Binds every id in routeManifest.ts to a component (and the ProtectedRoute
// guard). Split out from App.tsx so it can be exercised directly by
// routeBindings.test.tsx without mounting the whole provider stack --
// renderRouteManifest throws on a missing binding or guard, so that test
// asserts it does not throw for any manifest node.
//
// Every feature page below is bound here ONCE, in phase 0 (execution plan
// §8: "the mechanism that makes routeBindings.tsx freezable"). Deferred
// routes use named unavailable states so their route identity and recovery
// action stay truthful without implying that the future capability shipped.
import type { ComponentType } from 'react';
import { ProtectedRoute } from '../../auth/ProtectedRoute';
import { LoginPage } from '../../auth/LoginPage';
import { RootRedirect } from './RootRedirect';
import { RouteRecoveryScreen } from '../states/RouteStates';
import { AppShell } from '../shell/AppShell';
import { AccountsPage } from '../../features/accounts/AccountsPage';
import { AccountDetailPage } from '../../features/accounts/AccountDetailPage';
import { ConnectPage } from '../../features/connect/ConnectPage';
import { TrendPage } from '../../features/networth/TrendPage';
import { DayPage } from '../../features/networth/DayPage';
import { TransactionsPage } from '../../features/transactions/TransactionsPage';
import { TransactionDetailPage } from '../../features/transactions/TransactionDetailPage';
import { CategoriesPage } from '../../features/transactions/CategoriesPage';
import { MerchantsPage } from '../../features/transactions/MerchantsPage';
import { InboxPage } from '../../features/inbox/InboxPage';
import { SettingsPage } from '../../features/settings/SettingsPage';
import { BusinessPage } from '../../features/business/BusinessPage';
import { ConditionsPage } from '../../features/business/ConditionsPage';
import { ExportPage } from '../../features/business/ExportPage';
import { EventPage } from '../../features/business/EventPage';
import { defineRouteBinding } from './renderRouteManifest';

function withShell<P extends object>(Component: ComponentType<P>) {
  return function ShellWrapped(props: P) {
    return (
      <AppShell>
        <Component {...props} />
      </AppShell>
    );
  };
}

export const routeBindings = {
  login: defineRouteBinding(LoginPage),
  'callback-failure': defineRouteBinding(RouteRecoveryScreen, () => ({ kind: 'session-check-failure' })),
  unavailable: defineRouteBinding(RouteRecoveryScreen, () => ({ kind: 'api-unavailable' })),
  offline: defineRouteBinding(RouteRecoveryScreen, () => ({ kind: 'offline' })),
  'root-redirect': defineRouteBinding(withShell(RootRedirect)),
  connect: defineRouteBinding(withShell(ConnectPage)),
  accounts: defineRouteBinding(withShell(AccountsPage)),
  'accounts-day': defineRouteBinding(withShell(DayPage)),
  'accounts-trend': defineRouteBinding(withShell(TrendPage)),
  'account-detail': defineRouteBinding(withShell(AccountDetailPage)),
  activity: defineRouteBinding(withShell(TransactionsPage)),
  'spend-by-merchant': defineRouteBinding(withShell(MerchantsPage)),
  'activity-item': defineRouteBinding(withShell(TransactionDetailPage)),
  inbox: defineRouteBinding(withShell(InboxPage)),
  'spend-by-category': defineRouteBinding(withShell(CategoriesPage)),
  'business-year': defineRouteBinding(withShell(BusinessPage)),
  rules: defineRouteBinding(withShell(ConditionsPage)),
  'export-review': defineRouteBinding(withShell(ExportPage)),
  event: defineRouteBinding(withShell(EventPage)),
  settings: defineRouteBinding(withShell(SettingsPage)),
  'not-found': defineRouteBinding(RouteRecoveryScreen, () => ({ kind: 'not-found' })),
};

export const routeGuards = { ProtectedRoute };
